import React from "react";

import { css, cx } from "@emotion/css";
import { FieldPath, FieldValues, useFormContext, type PathValue, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useFormError } from "@/hooks/useFormError.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import type { Theme } from "@/theme/CreateTheme.ts";
import { FormInputError } from "@/uiKit/atoms/forms/FormInputError.tsx";
import { HTMLAutocomplete } from "@/uiKit/atoms/inputs/AutoComplete.ts";
import { TextInput, type TextFieldType } from "@/uiKit/atoms/inputs/TextInput.tsx";
import { Typography } from "@/uiKit/atoms/Typography.tsx";
import { AtomProps, StyleClasses } from "@/uiKit/atoms/utils/AtomTypes.ts";

export type BaseFormTextInputProps<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>> = {
  name: TName;

  labelType?: "default" | "left";
  label?: string;
  type?: TextFieldType;
  autoComplete?: HTMLAutocomplete;
  onChange?: (value: string | undefined, event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  autoFocus?: boolean;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  fullWidth?: boolean;
  reserveErrorSpace?: boolean;
};

export type FormTextInputProps<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>> = AtomProps<
  BaseFormTextInputProps<TFieldValues, TName>,
  StyleClasses<"root" | "label" | "content" | "error">
>;

export const FormTextInput = <TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>({
  id,
  name,
  label,
  labelType = "default",
  reserveErrorSpace,
  onChange,
  onBlur,
  disabled,
  className,
  classes,
  ...props
}: FormTextInputProps<TFieldValues, TName>): React.ReactElement => {
  const styles = useStyles(makeStyles);
  const { t } = useTranslation();
  const formContext = useFormContext<TFieldValues, TName>();
  const formError = useFormError(name);
  const value = useWatch({
    name,
    control: formContext.control,
  });

  return (
    <div className={cx({ [styles.container]: true, [styles.defaultLabel]: labelType === "default" }, className)}>
      <div className={cx(styles.content, classes?.content)}>
        {!!label && labelType === "left" && (
          <Typography
            variant="body"
            className={cx({ [styles.label]: true, [styles.labelError]: formError.hasError }, classes?.label)}
          >
            {label}
          </Typography>
        )}
        <TextInput
          id={id}
          className={cx(styles.root, className)}
          {...props}
          value={value ?? ""}
          onChange={(value, event) => {
            const newValue = value ? value : undefined;
            formContext.setValue(name, newValue as PathValue<TFieldValues, TName>, {
              shouldValidate: true,
              shouldDirty: true,
              shouldTouch: true,
            });
            onChange?.(newValue, event);
          }}
          onBlur={onBlur}
          label={labelType === "default" ? label : undefined}
          error={formError.hasError}
        />
      </div>
      <FormInputError
        reserveSpace={reserveErrorSpace}
        errorMessage={formError.errorMessage ? t(formError.errorMessage) : undefined}
        className={cx(styles.error, classes?.error)}
      />
    </div>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css({
    minWidth: 0,
  }),
  content: css({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    columnGap: theme.spacing(6),
    overflow: "clip",
  }),
  root: css({}),
  label: css({}),
  defaultLabel: css({
    marginTop: theme.spacing(-6),
  }),
  labelError: css({}),
  error: css({}),
});
